/**
 * @voltxampere/engine — Per-frequency complex MNA build (Sprint 20)
 *
 * The AC solver re-uses the DC `CircuitPlan` (topology never changes
 * with frequency) and rebuilds the complex admittance matrix Y(jω)
 * + RHS at every sweep point. Per-frequency cost is dominated by
 * stamp dispatch + complex LU; the planner runs ONCE.
 *
 * Stamp context:
 *  - `nodeIndex` mirrors the DC version: ground returns `-1`, known
 *    nets return `[0, nodeCount)`, unknown nets throw.
 *  - `vSourceIndex` returns the ABSOLUTE MNA row of the augmented
 *    branch-current equation: `nodeCount + internalNodeCount + idx`.
 *    Sprint 24 B1 unified this convention with the DC stamp context
 *    so circuits that mix BJT parasitic internal nodes with
 *    independent voltage sources land their augmented rows after
 *    the internal-node block, not on top of it.
 *  - `addY(i,j,re,im)` and `addRhs(i,re,im)` silently drop ground
 *    sentinel writes and accumulate complex contributions otherwise.
 *  - `omega` is fixed for the call; the solver iterates over
 *    `frequencies[]` and rebuilds per point.
 *  - `dcNodeVoltages` / `dcBranchCurrents` are read-only references
 *    to the DC OP — populated once by `linearizeAtOP`.
 *  - `temperature` defaults to 300.15 K (SPECS §3.5).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { complexMatrixAddRaw, createComplexMatrix, createComplexVector, } from "@voltxampere/math";
import { getACStampOrThrow } from "../stamps/register.js";
/**
 * Build the complex MNA system at angular frequency `omega`. Caller
 * is responsible for hot-loop allocations — every call returns a
 * fresh `Y` + `rhs` pair. Sparse-LU optimisations are deferred
 * (ADR-0038); this dense path keeps the implementation simple and
 * compatible with the small AC fixtures in the Sprint 20 corpus.
 */
export function buildACMatrix(circuit, plan, linearized, omega) {
    if (!Number.isFinite(omega) || omega < 0) {
        throw new ContractViolationError(`buildACMatrix: omega must be a non-negative finite number, got ${String(omega)}`);
    }
    const Y = createComplexMatrix(plan.size, plan.size);
    const rhs = createComplexVector(plan.size);
    const ctx = {
        size: plan.size,
        nodeCount: plan.nodeCount,
        nodeIndex: (netId) => {
            if (netId === plan.groundNetId)
                return -1;
            const idx = plan.netToIndex.get(netId);
            if (idx === undefined) {
                throw new ContractViolationError(`buildACMatrix.nodeIndex: unknown net "${netId}" (planner did not register it)`);
            }
            return idx;
        },
        vSourceIndex: (componentId) => {
            const idx = plan.vSourceIdToIndex.get(componentId);
            if (idx === undefined) {
                throw new ContractViolationError(`buildACMatrix.vSourceIndex: unknown vsource "${componentId}"`);
            }
            // Sprint 24 B1 — vsource rows live AFTER the internal-node
            // block: [external-KCL | internal-KCL | vsource-augmented].
            return plan.nodeCount + plan.internalNodeCount + idx;
        },
        addY: (i, j, re, im) => {
            if (i === -1 || j === -1)
                return;
            complexMatrixAddRaw(Y, i, j, re, im);
        },
        addRhs: (i, re, im) => {
            if (i === -1)
                return;
            const base = 2 * i;
            rhs.data[base] = (rhs.data[base] ?? 0) + re;
            rhs.data[base + 1] = (rhs.data[base + 1] ?? 0) + im;
        },
        omega,
        dcNodeVoltages: linearized.dcNodeVoltages,
        dcBranchCurrents: linearized.dcBranchCurrents,
        temperature: linearized.temperature,
    };
    for (const c of circuit.components) {
        const stamp = getACStampOrThrow(c.type);
        stamp(ctx, c);
    }
    return { Y, rhs };
}
//# sourceMappingURL=matrix.js.map